/**
 * RIFT — shared hand-authored SVG art.
 *
 * Every pixel here is inline SVG, no external assets, same rule mainmenu.js's
 * header documents for its own art: this guarantees an operator portrait and
 * a rank badge always render even if nothing else on the page does. Shared by
 * mainmenu.js (the home-screen operator-file portrait) and personnel.js (the
 * full roster) so there is exactly one drawing of each, not two that drift.
 */

/* ====================================================================== */
/* Operator hero — GUARANTEED SVG. Hand-authored three-quarter tactical    */
/* operator in a patrol carry, dramatic amber rim over a cold fill.        */
/* ====================================================================== */

export const OPERATOR_SVG = `
<svg class="rm-op" viewBox="0 0 360 560" preserveAspectRatio="xMidYMin slice" aria-hidden="true">
  <defs>
    <linearGradient id="rmFill" x1="0" y1="0" x2="1" y2="0.6">
      <stop offset="0" stop-color="#20272e"/>
      <stop offset="0.55" stop-color="#161b21"/>
      <stop offset="1" stop-color="#0c1014"/>
    </linearGradient>
    <linearGradient id="rmKit" x1="0" y1="0" x2="1" y2="0.4">
      <stop offset="0" stop-color="#2b333a"/>
      <stop offset="1" stop-color="#12171c"/>
    </linearGradient>
    <linearGradient id="rmRim" x1="1" y1="0" x2="0" y2="1">
      <stop offset="0" stop-color="#ffce6b"/>
      <stop offset="0.5" stop-color="#ffb02a"/>
      <stop offset="1" stop-color="#c77d10"/>
    </linearGradient>
    <radialGradient id="rmVisor" cx="0.4" cy="0.4" r="0.7">
      <stop offset="0" stop-color="#8fe6ff"/>
      <stop offset="0.6" stop-color="#2b7ba0"/>
      <stop offset="1" stop-color="#0c2733"/>
    </radialGradient>
  </defs>

  <!-- ground contact -->
  <ellipse cx="182" cy="548" rx="120" ry="16" fill="#000" opacity="0.55"/>

  <g stroke="#05080b" stroke-width="1.4" stroke-linejoin="round">
    <!-- LEGS -->
    <path d="M150 348 L138 470 L128 540 L166 540 L172 470 L178 392 Z" fill="url(#rmFill)"/>
    <path d="M182 352 L196 470 L206 540 L242 540 L228 456 L214 356 Z" fill="url(#rmKit)"/>
    <!-- knee pads -->
    <path d="M141 430 q-6 22 4 34 q18 4 24 -8 q4 -20 -6 -30 z" fill="url(#rmKit)"/>
    <path d="M198 432 q-4 22 6 34 q18 3 24 -10 q2 -20 -8 -28 z" fill="url(#rmKit)"/>
    <!-- boots -->
    <path d="M126 528 L120 552 L172 552 L170 528 Z" fill="#0b0f13"/>
    <path d="M204 528 L206 552 L250 552 L244 528 Z" fill="#0b0f13"/>

    <!-- HIPS / BELT -->
    <path d="M142 336 L150 372 L214 372 L222 336 Z" fill="url(#rmKit)"/>
    <rect x="150" y="344" width="64" height="12" fill="#0d1216"/>
    <!-- drop pouch -->
    <path d="M150 372 l-6 34 l26 0 l2 -34 z" fill="url(#rmFill)"/>

    <!-- TORSO / PLATE CARRIER -->
    <path d="M138 214 Q134 200 150 192 L214 192 Q232 200 226 218 L228 300 Q228 336 214 344 L150 344 Q140 336 140 300 Z" fill="url(#rmFill)"/>
    <!-- carrier front panel -->
    <path d="M150 208 L214 208 L220 300 Q214 330 200 332 L164 332 Q150 328 148 300 Z" fill="url(#rmKit)"/>
    <!-- mag pouches -->
    <rect x="156" y="270" width="20" height="46" rx="3" fill="#0e1317"/>
    <rect x="180" y="270" width="20" height="46" rx="3" fill="#11171c"/>
    <rect x="168" y="236" width="30" height="26" rx="3" fill="#0e1317"/>
    <!-- admin/radio -->
    <rect x="150" y="230" width="16" height="24" rx="2" fill="#141a1f"/>
    <path d="M158 224 l0 -16" stroke="#05080b" stroke-width="2"/>

    <!-- SHOULDERS -->
    <path d="M150 192 Q128 194 120 214 L134 250 L150 244 Z" fill="url(#rmKit)"/>
    <path d="M214 192 Q238 196 244 218 L232 252 L214 244 Z" fill="url(#rmKit)"/>

    <!-- NECK -->
    <path d="M168 176 L166 196 L198 196 L196 176 Z" fill="#1a2026"/>

    <!-- HEAD: helmet + goggles DOWN + face wrap (vanguard) -->
    <g>
      <path d="M156 96 Q158 66 190 66 Q220 68 222 100 L222 120 L156 122 Z" fill="url(#rmKit)"/>
      <path d="M214 92 l16 -4 l2 8 l-16 6 z" fill="#0f151a"/>
      <circle cx="164" cy="84" r="5" fill="#0f151a"/>
      <path d="M158 132 Q160 168 190 172 Q216 168 220 138 L218 120 L158 122 Z" fill="url(#rmFill)"/>
      <path d="M156 116 L224 112 L224 134 Q190 142 156 134 Z" fill="#0a0e12"/>
      <path d="M168 118 Q190 124 214 118 L214 130 Q190 136 168 130 Z" fill="url(#rmVisor)" opacity="0.9"/>
      <path d="M156 122 L146 130 L150 138 L158 132 Z" fill="#0d1216"/>
    </g>

    <!-- SUPPORT (left) ARM forward onto handguard -->
    <path d="M226 220 Q252 230 250 262 L232 300 Q222 302 214 292 L226 258 Z" fill="url(#rmFill)"/>
    <path d="M216 288 q-10 8 -6 22 q14 8 26 -2 q6 -12 -2 -22 z" fill="#0d1216"/>

    <!-- RIFLE (patrol carry): stock high-right, barrel low-left -->
    <g>
      <path d="M236 250 L150 322" stroke="#0b0f13" stroke-width="13" stroke-linecap="round"/>
      <path d="M236 250 L150 322" stroke="#1b2126" stroke-width="7" stroke-linecap="round"/>
      <path d="M196 286 L172 306" stroke="#05080b" stroke-width="2"/>
      <path d="M242 240 l20 -6 l6 16 l-20 8 z" fill="#0e1317"/>
      <path d="M204 278 l-8 30 l14 4 l10 -30 z" fill="#0d1216"/>
      <rect x="214" y="256" width="18" height="12" rx="2" transform="rotate(-40 223 262)" fill="#11171c"/>
      <circle cx="150" cy="322" r="5" fill="#05080b"/>
    </g>

    <!-- FIRING (right) ARM: upper arm down to grip -->
    <path d="M138 216 Q116 226 118 258 L138 300 Q150 302 156 290 L146 256 Z" fill="url(#rmFill)"/>
    <path d="M138 292 Q150 316 178 316 L188 300 Q170 300 156 288 Z" fill="url(#rmKit)"/>
    <path d="M180 300 q14 2 18 16 q-6 12 -20 8 q-10 -8 -6 -20 z" fill="#0d1216"/>
    <!-- sling -->
    <path d="M150 200 Q120 250 150 322" stroke="#0a0e12" stroke-width="3" fill="none"/>
  </g>

  <!-- AMBER RIM LIGHT: strokes down the camera-left silhouette -->
  <g fill="none" stroke="url(#rmRim)" stroke-width="2.4" stroke-linecap="round" opacity="0.95" class="rm-op-rim">
    <path d="M156 96 Q158 66 190 66"/>
    <path d="M156 122 L156 134"/>
    <path d="M150 192 Q128 194 120 214 L134 250"/>
    <path d="M138 216 Q116 226 118 258 L138 300"/>
    <path d="M142 336 L138 470 L128 540"/>
    <path d="M150 348 L138 470"/>
  </g>
  <!-- cool cyan kicker on the visor -->
  <path d="M168 118 Q190 124 214 118" fill="none" stroke="#8fe6ff" stroke-width="2" opacity="0.7"/>
</svg>`;

/* ---- rank chevrons (SVG) --------------------------------------------- */
export const RANK_SVG = `
<svg viewBox="0 0 44 44" aria-hidden="true">
  <path d="M22 4 L40 40 L4 40 Z" fill="none" stroke="#ffb02a" stroke-width="2" opacity="0.55"/>
  <g fill="none" stroke="#ffb02a" stroke-width="2.4" stroke-linecap="round">
    <path d="M12 20 L22 15 L32 20"/>
    <path d="M12 27 L22 22 L32 27"/>
    <path d="M12 34 L22 29 L32 34"/>
  </g>
</svg>`;
